import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Hammer, Menu, X, User, LogOut, Settings, Bookmark, 
  ChevronDown, Type, Code2, Search, Sparkles, Repeat, Trophy
} from 'lucide-react';
import { useState } from 'react';

const Navbar = () => {
  const { user, login, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { href: '/tools', label: 'Tools' },
    { href: '/all-tools', label: 'All Tools' },
    { href: '/pricing', label: 'Pricing' },
    { href: '/blog', label: 'Blog' },
  ];

  const categoryLinks = [
    { href: '/category/text-tools', label: 'Text Tools', icon: Type },
    { href: '/category/developer-tools', label: 'Developer Tools', icon: Code2 },
    { href: '/category/seo-tools', label: 'SEO Tools', icon: Search },
    { href: '/category/generators', label: 'Generators', icon: Sparkles },
    { href: '/category/converters', label: 'Converters', icon: Repeat },
  ];

  const comparisonLinks = [
    { href: '/compare/best-free-online-tools', label: 'Best Free Tools', icon: Trophy },
    { href: '/compare/best-developer-tools', label: 'Best Developer Tools', icon: Code2 },
    { href: '/compare/best-seo-tools', label: 'Best SEO Tools', icon: Search },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Hammer className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold font-['Plus_Jakarta_Sans']">ToolsForge</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/tools"
              className={`nav-link text-sm font-medium ${isActive('/tools') ? 'text-foreground' : ''}`}
              data-testid="nav-tools"
            >
              Tools
            </Link>
            
            {/* Categories Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 nav-link text-sm font-medium" data-testid="nav-categories">
                Categories
                <ChevronDown className="w-3.5 h-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                {categoryLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <DropdownMenuItem key={link.href} asChild>
                      <Link to={link.href} className="flex items-center gap-2" data-testid={`nav-category-${link.label.toLowerCase().replace(' ', '-')}`}>
                        <Icon className="w-4 h-4 text-primary" />
                        {link.label}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/all-tools" className="flex items-center gap-2 text-primary">
                    View All Tools
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Best Tools Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 nav-link text-sm font-medium" data-testid="nav-best-tools">
                Best Tools
                <ChevronDown className="w-3.5 h-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                {comparisonLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <DropdownMenuItem key={link.href} asChild>
                      <Link to={link.href} className="flex items-center gap-2" data-testid={`nav-compare-${link.label.toLowerCase().replace(' ', '-')}`}>
                        <Icon className="w-4 h-4 text-amber-500" />
                        {link.label}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/pricing"
              className={`nav-link text-sm font-medium ${isActive('/pricing') ? 'text-foreground' : ''}`}
              data-testid="nav-pricing"
            >
              Pricing
            </Link>
            <Link
              to="/blog"
              className={`nav-link text-sm font-medium ${isActive('/blog') ? 'text-foreground' : ''}`}
              data-testid="nav-blog"
            >
              Blog
            </Link>
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4" />
                      </div>
                    )}
                    <span className="text-sm">{user.name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2" data-testid="menu-dashboard">
                      <Settings className="w-4 h-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard?tab=saved" className="flex items-center gap-2" data-testid="menu-saved">
                      <Bookmark className="w-4 h-4" />
                      Saved Tools
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="flex items-center gap-2 text-destructive" data-testid="menu-logout">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button onClick={login} className="glow-blue" data-testid="login-button">
                Sign In with Google
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-border pt-4">
            <div className="flex flex-col gap-4">
              <Link
                to="/tools"
                className="text-sm font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setMobileOpen(false)}
              >
                Tools
              </Link>
              <Link
                to="/all-tools"
                className="text-sm font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setMobileOpen(false)}
              >
                All Tools
              </Link>
              
              {/* Mobile Categories */}
              <div className="pl-2 border-l-2 border-border space-y-2">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Categories</span>
                {categoryLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block text-sm text-muted-foreground hover:text-foreground"
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              {/* Mobile Best Tools */}
              <div className="pl-2 border-l-2 border-amber-500/30 space-y-2">
                <span className="text-xs uppercase tracking-wider text-amber-500">Best Tools</span>
                {comparisonLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block text-sm text-muted-foreground hover:text-foreground"
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              <Link
                to="/pricing"
                className="text-sm font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setMobileOpen(false)}
              >
                Pricing
              </Link>
              <Link
                to="/blog"
                className="text-sm font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setMobileOpen(false)}
              >
                Blog
              </Link>
              {user ? (
                <>
                  <Link to="/dashboard" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>
                    Dashboard
                  </Link>
                  <button onClick={logout} className="text-sm font-medium text-destructive text-left">
                    Logout
                  </button>
                </>
              ) : (
                <Button onClick={login} className="w-full" data-testid="mobile-login-button">
                  Sign In with Google
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
